import {
  GuidanceFileType,
  SessionInferenceMode,
} from "@/components/graphql/schema.graphql";
import { NO_TRANSLATION_PROMPT_LANGUAGE } from "@/components/inference-forms/constants";
import {
  formatAPIPromptLanguageForForm,
  getPercentageFromWeight,
  getSimilarityFromWeight,
  getVideoObject,
  REFERENCE_VIDEO_GUIDANCE_FILE_TYPES,
} from "@/components/inference-forms/utils/utils";
import {
  DEFAULT_AUDIO_DURATION_VALUE,
  DEFAULT_AUTO_DURATION,
} from "@/components/InferenceFormComposer/FieldDefs/fieldDefs/AudioDuration";
import { FieldValueTypes } from "@/components/InferenceFormComposer/FieldDefs/FieldValueTypes";
import {
  ReferenceType,
  ReferenceTypeEnum,
} from "@/components/ui-v2/References/types";
import { getDefaultReference } from "@/components/ui-v2/References/utils";
import {
  FileReferenceType,
  GuidanceFileParameters,
  InferenceParameters,
  SessionStateInferenceParameters,
} from "@/types";
import { getImageObjectSafe } from "@/utils/images";

export const convertGuidanceFilesToFileReferences = async (
  files: GuidanceFileParameters[],
): Promise<FileReferenceType[]> => {
  const results = await Promise.all(
    files.map(async (file) => {
      const imageObject = await getImageObjectSafe(file.url);
      if (!imageObject?.width || !imageObject.height) return null;
      const ref: FileReferenceType = {
        driveFileId: file.fileId,
        src: file.url,
        width: imageObject.width,
        height: imageObject.height,
        transform: file.transform,
      };
      return ref;
    }),
  );
  return results.filter((ref) => ref !== null);
};

const convertGuidanceFilesToVideoReferences = async (
  files: GuidanceFileParameters[],
): Promise<FileReferenceType[]> => {
  const results = await Promise.all(
    files.map(async (file) => {
      try {
        const videoObject = await getVideoObject(file.url);
        const ref: FileReferenceType = {
          driveFileId: file.fileId,
          src: file.url,
          width: videoObject.videoWidth,
          height: videoObject.videoHeight,
        };
        return ref;
      } catch {
        console.error(`Failed to get video object for file ${file.url}`);
        return null;
      }
    }),
  );
  return results.filter((ref) => ref !== null);
};

const convertCommonParametersToFormValues = (
  params: SessionStateInferenceParameters,
): Partial<FieldValueTypes> => {
  const formValues: Partial<FieldValueTypes> = {};

  if (typeof params.prompt !== "undefined") {
    formValues.prompt = {
      prompt: params.prompt,
      promptJson: params.promptJson ?? null,
      promptLanguage:
        typeof params.promptLanguage !== "undefined"
          ? formatAPIPromptLanguageForForm(params.promptLanguage)
          : NO_TRANSLATION_PROMPT_LANGUAGE,
      negativePrompt: params.negativePrompt ?? null,
      negativePromptJson: params.negativePromptJson ?? null,
    };
  }

  if (params.styles?.length) {
    const weightPercentage = getPercentageFromWeight(params.styles[0].weight);
    formValues.model = {
      id: params.styles[0].id,
      weight: weightPercentage,
    };
    // Mirror onto the user-facing `modelWeight` slider so forms that
    // expose it (e.g. PROMPT_EDIT_IMAGE) restore the saved value.
    formValues.modelWeight = weightPercentage;
  }

  if (params.width && params.height) {
    formValues.dimensions = [params.width, params.height];
  }

  if (typeof params.upscaleRatio !== "undefined") {
    formValues.scaleFactor = params.upscaleRatio;
    formValues.scaleFactorVideo = params.upscaleRatio;
  }

  if (typeof params.targetFps !== "undefined") {
    formValues.targetFps = params.targetFps;
  }

  if (typeof params.creativity !== "undefined") {
    const creativityPercentage = getPercentageFromWeight(params.creativity);
    formValues.upscaleCreativity = creativityPercentage;
    formValues.creativity = creativityPercentage;
  }

  if (typeof params.realtimeMode !== "undefined") {
    formValues.realtimeMode = params.realtimeMode;
  }

  if (typeof params.resemblance !== "undefined") {
    formValues.upscaleResemblance = getPercentageFromWeight(params.resemblance);
  }

  if (typeof params.guidanceScale !== "undefined") {
    formValues.guidanceScale = params.guidanceScale;
  }

  if (typeof params.numInferenceSteps !== "undefined") {
    formValues.inferenceSteps = params.numInferenceSteps;
  }

  if (typeof params.quadMesh !== "undefined") {
    formValues.quadMesh = params.quadMesh;
  }
  if (typeof params.faceLimit !== "undefined") {
    formValues.faceLimit = params.faceLimit;
  }
  if (typeof params.includeTextures !== "undefined") {
    formValues.texture = params.includeTextures;
  }
  if (typeof params.poseMode !== "undefined") {
    formValues.poseMode = params.poseMode;
  } else if (params.useTaPose) {
    // Older 3D generations stored the boolean `useTaPose`. Restore it as T_POSE (the
    // common rig pose) — these params carry no base model to distinguish Tripo's A-pose,
    // mirroring the backend's model-less migration default.
    formValues.poseMode = "T_POSE";
  }
  if (params.textureResolution != null) {
    formValues.textureResolution = params.textureResolution;
  }
  if (typeof params.removeLighting === "boolean") {
    formValues.removeLighting = params.removeLighting;
  }
  if (params.meshDensity != null) {
    formValues.meshDensity = params.meshDensity;
  }
  if (typeof params.preserveUvs === "boolean") {
    formValues.preserveUvs = params.preserveUvs;
  }
  if (typeof params.lowPoly !== "undefined") {
    formValues.lowPoly = params.lowPoly;
  }
  if (typeof params.generateParts !== "undefined") {
    formValues.generateParts = params.generateParts;
  }
  if (typeof params.pbrMaterials !== "undefined") {
    formValues.pbrMaterials = params.pbrMaterials;
  }
  if (typeof params.enableRigging !== "undefined") {
    formValues.enableRigging = params.enableRigging;
  }
  if (typeof params.enableAnimation !== "undefined") {
    formValues.enableAnimation = params.enableAnimation;
  }

  if (typeof params.stability !== "undefined") {
    formValues.stability = params.stability * 100;
  }
  if (typeof params.similarityBoost !== "undefined") {
    formValues.similarityBoost = params.similarityBoost * 100;
  }
  if (typeof params.useSpeakerBoost !== "undefined") {
    formValues.speakerBoost = params.useSpeakerBoost;
  }
  if (typeof params.styleExaggeration !== "undefined") {
    formValues.styleExaggeration = params.styleExaggeration * 100;
  }
  if (typeof params.speed !== "undefined") {
    formValues.audioSpeed = params.speed;
  }
  if (typeof params.promptStrength !== "undefined") {
    formValues.promptStrength = params.promptStrength * 100;
  }
  if (
    typeof params.durationSeconds !== "undefined" ||
    typeof params.autoDuration !== "undefined"
  ) {
    formValues.audioDuration = {
      value: params.durationSeconds ?? DEFAULT_AUDIO_DURATION_VALUE,
      isAuto: params.autoDuration ?? DEFAULT_AUTO_DURATION,
    };
  }

  if (params.multiPrompt?.length) {
    formValues.multiPrompt = params.multiPrompt.map((entry) => ({
      ...entry,
      promptJson: entry.promptJson ?? null,
    }));
  }

  if (typeof params.generateAudio !== "undefined") {
    formValues.generateAudio = params.generateAudio;
  }

  if (typeof params.keepAudio !== "undefined") {
    formValues.keepAudio = params.keepAudio;
  }

  if (typeof params.videoEffects !== "undefined") {
    formValues.videoEffects = params.videoEffects;
  }

  if (typeof params.durationSeconds !== "undefined") {
    formValues.videoDuration = params.durationSeconds;
  }

  if (typeof params.fps !== "undefined") {
    formValues.fps = params.fps;
  }

  if (typeof params.batchSize !== "undefined") {
    formValues.batchSize = params.batchSize;
  }

  if (typeof params.transparency !== "undefined") {
    formValues.transparency = params.transparency;
  }

  if (typeof params.tileability !== "undefined") {
    formValues.tileability = params.tileability;
  }

  if (typeof params.quality !== "undefined") {
    formValues.performance = params.quality;
  }

  if (typeof params.baseShift !== "undefined") {
    formValues.baseShift = params.baseShift;
  }

  if (typeof params.maxShift !== "undefined") {
    formValues.maxShift = params.maxShift;
  }

  return formValues;
};

const convertFilesToSourceImages = async (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
): Promise<void> => {
  const initFiles = params.files?.filter((f) => f.type === "INIT_IMAGE") ?? [];

  if (initFiles.length) {
    formValues.sourceImages =
      await convertGuidanceFilesToFileReferences(initFiles);
  } else {
    formValues.sourceImages = [];
  }
};

const convertFilesToSourceVideos = async (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
): Promise<void> => {
  const initVideoFiles =
    params.files?.filter((f) => f.type === "INIT_VIDEO") ?? [];

  if (initVideoFiles.length) {
    formValues.sourceVideos =
      await convertGuidanceFilesToVideoReferences(initVideoFiles);
  } else {
    formValues.sourceVideos = [];
  }
};

const convertFilesToReferenceVideos = async (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
): Promise<void> => {
  const referenceVideoFiles =
    params.files?.filter((f) =>
      REFERENCE_VIDEO_GUIDANCE_FILE_TYPES.includes(f.type),
    ) ?? [];

  if (referenceVideoFiles.length) {
    formValues.referenceVideos =
      await convertGuidanceFilesToVideoReferences(referenceVideoFiles);
  } else {
    formValues.referenceVideos = [];
  }
};

const convertFilesToSourceMeshes = (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
): void => {
  const meshFiles = params.files?.filter((f) => f.type === "INIT_MESH") ?? [];

  formValues.sourceMeshes = meshFiles.map((f) => ({
    src: f.url,
    driveFileId: f.fileId,
  }));
};

const convertFilesToGuidanceFiles = async (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
  guidanceFileType: GuidanceFileType,
): Promise<void> => {
  const guidanceFiles =
    params.files?.filter((file) => file.type === guidanceFileType) ?? [];

  if (guidanceFiles.length) {
    formValues.guidanceFiles =
      await convertGuidanceFilesToFileReferences(guidanceFiles);
  } else {
    formValues.guidanceFiles = [];
  }
};

const convertFilesToFirstFrame = async (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
): Promise<void> => {
  const selectedFile = params.files?.find((f) => f.type === "FIRST_FRAME");

  if (selectedFile) {
    const refs = await convertGuidanceFilesToFileReferences([selectedFile]);
    formValues.firstFrame = refs[0] ?? null;
  } else {
    formValues.firstFrame = null;
  }
};

const convertFilesToLastFrame = async (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
): Promise<void> => {
  const lastFrameFiles =
    params.files?.filter((f) => f.type === "LAST_FRAME") ?? [];

  if (lastFrameFiles.length) {
    const refs = await convertGuidanceFilesToFileReferences([
      lastFrameFiles[0],
    ]);
    formValues.lastFrame = refs[0] ?? null;
  } else {
    formValues.lastFrame = null;
  }
};

const convertFilesToReferences = async (
  params: InferenceParameters,
): Promise<ReferenceType[]> => {
  if (!params.files?.length) return [];

  return Promise.all(
    params.files.map(async (file) => {
      const type = file.type as ReferenceTypeEnum;
      const similarity = getSimilarityFromWeight(file.weight);
      let image: ReferenceType["image"] = null;

      const imageObject = await getImageObjectSafe(file.url);
      if (imageObject) {
        image = {
          driveFileId: file.fileId,
          src: file.url,
          width: imageObject.width,
          height: imageObject.height,
          transform: file.transform,
        };
      }
      return getDefaultReference({ type, image, similarity });
    }),
  );
};

const convertFilesToAudioTrack = (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
): void => {
  const audioFiles =
    params.files?.filter((f) => f.type === "REFERENCE_AUDIO") ?? [];

  if (audioFiles.length) {
    formValues.audioTrack = {
      src: audioFiles[0].url,
      driveFileId: audioFiles[0].fileId,
    };
  } else {
    formValues.audioTrack = null;
  }
};

const ELEMENT_TYPES = new Set<GuidanceFileType>([
  "ELEMENT_FRONTAL_IMAGE",
  "ELEMENT_REFERENCE_IMAGE",
  "ELEMENT_VIDEO",
]);

const convertFilesToElements = async (
  params: InferenceParameters,
  formValues: Partial<FieldValueTypes>,
): Promise<void> => {
  const elementFiles =
    params.files?.filter((f) => ELEMENT_TYPES.has(f.type)) ?? [];
  if (!elementFiles.length) return;

  const grouped = new Map<string, typeof elementFiles>();
  for (const file of elementFiles) {
    if (file.group == null) continue;
    if (!grouped.has(file.group)) grouped.set(file.group, []);
    grouped.get(file.group)!.push(file);
  }

  formValues.elements = (
    await Promise.all(
      [...grouped.entries()]
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(async ([, files]) => {
          const frontalFile = files.find(
            (f) => f.type === "ELEMENT_FRONTAL_IMAGE",
          );
          const videoFile = files.find((f) => f.type === "ELEMENT_VIDEO");

          let frontalImage: FileReferenceType | null = null;
          if (frontalFile) {
            const imageObject = await getImageObjectSafe(frontalFile.url);
            if (imageObject) {
              frontalImage = {
                src: frontalFile.url,
                driveFileId: frontalFile.fileId,
                width: imageObject.width,
                height: imageObject.height,
              };
            }
          }

          const referenceImages = (
            await Promise.all(
              files
                .filter((f) => f.type === "ELEMENT_REFERENCE_IMAGE")
                .map(async (f) => {
                  const imageObject = await getImageObjectSafe(f.url);
                  if (!imageObject) return null;
                  const ref: FileReferenceType = {
                    src: f.url,
                    driveFileId: f.fileId,
                    width: imageObject.width,
                    height: imageObject.height,
                  };
                  return ref;
                }),
            )
          ).filter((ref) => ref !== null);

          let video: FileReferenceType | null = null;
          if (videoFile) {
            try {
              const videoObject = await getVideoObject(videoFile.url);
              video = {
                src: videoFile.url,
                driveFileId: videoFile.fileId,
                width: videoObject.videoWidth,
                height: videoObject.videoHeight,
              };
            } catch {
              // ignore invalid video files from back-end
            }
          }

          if (
            frontalImage == null &&
            referenceImages.length === 0 &&
            video == null
          ) {
            return null;
          }

          return { frontalImage, referenceImages, video };
        }),
    )
  ).filter((el) => el !== null);
};

export const convertInferenceParametersToFormValues = async (
  params: SessionStateInferenceParameters,
  mode?: SessionInferenceMode,
): Promise<Partial<FieldValueTypes>> => {
  const formValues = convertCommonParametersToFormValues(params);

  switch (mode) {
    case "UPSCALE_VIDEO":
    case "REFRAME_VIDEO":
    case "REMOVE_BACKGROUND_VIDEO":
    case "ADD_SFX_VIDEO":
      await convertFilesToSourceVideos(params, formValues);
      break;
    case "UPSCALE":
    case "REMOVE_BACKGROUND":
    case "VECTORIZE_IMAGE":
    case "REFRAME_IMAGE":
      await convertFilesToSourceImages(params, formValues);
      break;
    case "PROMPT_EDIT_VIDEO":
      await convertFilesToSourceVideos(params, formValues);
      await convertFilesToGuidanceFiles(params, formValues, "REFERENCE_IMAGE");
      await convertFilesToElements(params, formValues);
      break;
    case "PROMPT_EDIT_IMAGE":
      await convertFilesToSourceImages(params, formValues);
      await convertFilesToGuidanceFiles(params, formValues, "REFERENCE_IMAGE");
      break;
    case "RETEXTURE_MESH":
      convertFilesToSourceMeshes(params, formValues);
      await convertFilesToGuidanceFiles(params, formValues, "TEXTURE_IMAGE");
      break;
    case "CREATE_3_D":
      await convertFilesToGuidanceFiles(params, formValues, "INIT_IMAGE");
      break;
    case "REMESH":
    case "RIG_MESH":
      convertFilesToSourceMeshes(params, formValues);
      break;
    case "CREATE_VIDEO":
      await convertFilesToFirstFrame(params, formValues);
      await convertFilesToLastFrame(params, formValues);
      convertFilesToAudioTrack(params, formValues);
      await convertFilesToElements(params, formValues);
      await convertFilesToGuidanceFiles(params, formValues, "REFERENCE_IMAGE");
      await convertFilesToReferenceVideos(params, formValues);
      break;
    case "CREATE":
      formValues.references = await convertFilesToReferences(params);
      if (params.styles?.length) {
        formValues.modelBlends = params.styles.map((s) => ({
          id: s.id,
          weight: getPercentageFromWeight(s.weight),
        }));
      }
      break;
    case "CREATE_SPEECH":
    case "CREATE_SOUND_EFFECT":
      break;
    default:
      break;
  }

  return formValues;
};
